import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const chaosWords = [
  { text: "INCONSISTENT", x: "10%", y: "20%", rotate: -5, delay: 0 },
  { text: "GUESSWORK", x: "60%", y: "15%", rotate: 3, delay: 0.1 },
  { text: "ONE-OFF", x: "25%", y: "45%", rotate: -2, delay: 0.2 },
  { text: "RANDOM", x: "70%", y: "50%", rotate: 4, delay: 0.3 },
  { text: "CHAOS", x: "15%", y: "70%", rotate: -3, delay: 0.4 },
  { text: "SCATTERED", x: "55%", y: "75%", rotate: 2, delay: 0.5 },
];

const SceneProblem = () => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.95, 1, 1, 0.95]);

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-screen flex items-center justify-center overflow-hidden"
      id="problem"
    >
      {/* Darker, more oppressive background */}
      <div className="absolute inset-0 bg-gradient-to-b from-frmla-dark via-background to-frmla-dark" />
      
      {/* Glitch lines */}
      <div className="absolute inset-0 overflow-hidden">
        {[
          { top: 20, x: 8, duration: 2.3 },
          { top: 35, x: -6, duration: 2.7 },
          { top: 50, x: 12, duration: 2.1 },
          { top: 65, x: -9, duration: 2.5 },
          { top: 80, x: 5, duration: 2.9 }
        ].map((line, i) => (
          <motion.div
            key={i}
            className="absolute w-full h-px bg-muted-foreground/20"
            style={{ top: `${line.top}%` }}
            animate={{ 
              x: [0, line.x, 0],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{ 
              duration: line.duration, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
      
      <motion.div 
        className="relative z-10 w-full h-full min-h-screen flex flex-col items-center justify-center"
        style={{ opacity, scale }}
      >
        {/* Central question */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12 px-6"
        >
          <h2 className="font-display text-subhero text-muted-foreground/60 mb-4">
            EVERY PROJECT
          </h2>
          <h2 className="font-display text-hero text-foreground glitch" data-text="STARTS FROM ZERO?">
            STARTS FROM ZERO?
          </h2>
        </motion.div>
        
        {/* Chaos words floating */}
        <div className="relative w-full max-w-6xl h-[400px] mx-auto">
          {chaosWords.map((word, index) => (
            <motion.div
              key={index}
              className="absolute font-display text-2xl md:text-4xl text-muted-foreground/30 chaos-element select-none"
              style={{ 
                left: word.x, 
                top: word.y,
                rotate: word.rotate
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.8, 
                delay: word.delay,
                ease: [0.16, 1, 0.3, 1]
              }}
              viewport={{ once: true }}
            >
              {word.text}
            </motion.div>
          ))}
          
          {/* Broken grid lines */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <motion.line 
              x1="10" y1="30" x2="40" y2="35" 
              stroke="hsl(var(--muted-foreground))" 
              strokeWidth="0.2"
              strokeDasharray="2 2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 0.3 }}
              transition={{ duration: 1.5 }}
              viewport={{ once: true }}
            />
            <motion.line 
              x1="60" y1="25" x2="90" y2="30" 
              stroke="hsl(var(--muted-foreground))" 
              strokeWidth="0.2"
              strokeDasharray="2 2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 0.3 }}
              transition={{ duration: 1.5, delay: 0.2 }}
              viewport={{ once: true }}
            />
            <motion.line 
              x1="20" y1="70" x2="50" y2="65" 
              stroke="hsl(var(--muted-foreground))" 
              strokeWidth="0.2"
              strokeDasharray="2 2"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 0.3 }}
              transition={{ duration: 1.5, delay: 0.4 }}
              viewport={{ once: true }}
            />
          </svg>
        </div>
        
        {/* Subtle message */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="font-body text-sm md:text-base text-muted-foreground/50 tracking-wider text-center px-6 max-w-xl"
        >
          No structure. No system. No repeatability.
        </motion.p>
      </motion.div>
    </section>
  );
};

export default SceneProblem;
